import { z } from 'zod';
import { configKey, editableText, entityId, isoDateTime, publishable } from './primitives.js';

/**
 * CRM E KANBAN (Cap. 13)
 *
 * O pipeline nao e um enum. E uma lista de etapas que o administrador cria,
 * renomeia, reordena e apaga pelo painel — inclusive criando pipelines
 * paralelos (ex.: "Venda", "Fee mensal 90 dias", "Compra de terreno").
 */

export const pipelineStage = z.object({
  key: configKey,
  name: editableText,
  /** Cor do cabecalho da coluna (token do tema ou hex). */
  color: z.string().default('neutral'),
  order: z.number().int().default(0),
  /** Etapas terminais fecham o lead. */
  outcome: z.enum(['open', 'won', 'lost']).default('open'),
  /** Dias parado na etapa antes de sinalizar atraso. */
  slaDays: z.number().int().min(0).default(0),
  /** Exige preencher estes campos para sair da etapa. */
  requiredFields: z.array(configKey).default([]),
  /** Automacoes disparadas ao entrar na etapa. */
  onEnter: z
    .array(
      z.discriminatedUnion('kind', [
        z.object({ kind: z.literal('notify'), templateKey: configKey, audience: z.enum(['owner', 'lead', 'backoffice']) }),
        z.object({ kind: z.literal('create_task'), title: editableText, dueInDays: z.number().int().min(0).default(1) }),
        z.object({ kind: z.literal('assign_round_robin'), teamKey: configKey }),
        z.object({ kind: z.literal('webhook'), endpointKey: configKey }),
      ]),
    )
    .default([]),
  publication: publishable.default({}),
});

export type PipelineStage = z.infer<typeof pipelineStage>;

export const pipeline = z.object({
  id: entityId,
  key: configKey,
  name: editableText,
  description: editableText.nullable().default(null),
  stages: z.array(pipelineStage).default([]),
  /** Pipeline que recebe leads sem roteamento explicito. */
  isDefault: z.boolean().default(false),
  /** Campos extras do lead neste pipeline — reusa o motor de campos. */
  customFieldsSchemaKey: configKey.nullable().default(null),
  publication: publishable.default({}),
});

export type Pipeline = z.infer<typeof pipeline>;

export const leadSource = z.enum([
  'listing_form',
  'listing_whatsapp',
  'listing_phone',
  'match',
  'natural_language_search',
  'aging_opportunity',
  'manual',
  'import',
  'campaign',
]);

export type LeadSource = z.infer<typeof leadSource>;

export const leadActivity = z.object({
  id: entityId,
  kind: z.enum(['note', 'call', 'whatsapp', 'email', 'visit', 'proposal', 'stage_change', 'task', 'system']),
  content: z.string(),
  authorId: entityId.nullable().default(null),
  authorName: z.string().nullable().default(null),
  createdAt: isoDateTime,
  metadata: z.record(z.string(), z.unknown()).default({}),
});

export type LeadActivity = z.infer<typeof leadActivity>;

export const lead = z.object({
  id: entityId,
  pipelineKey: configKey,
  stageKey: configKey,
  name: z.string(),
  phone: z.string().nullable().default(null),
  whatsapp: z.string().nullable().default(null),
  email: z.string().nullable().default(null),
  source: leadSource,
  /** Vinculo com o anuncio que originou o lead (Cap. 2.2 — o codigo acompanha). */
  listingId: entityId.nullable().default(null),
  listingCode: z.string().nullable().default(null),
  advertiserId: entityId.nullable().default(null),
  matchId: entityId.nullable().default(null),
  ownerId: entityId.nullable().default(null),
  ownerName: z.string().nullable().default(null),
  notes: z.string().nullable().default(null),
  customFields: z.record(z.string(), z.unknown()).default({}),
  lastInteractionAt: isoDateTime.nullable().default(null),
  nextActionAt: isoDateTime.nullable().default(null),
  nextActionTitle: z.string().nullable().default(null),
  valueCents: z.number().int().min(0).nullable().default(null),
  activities: z.array(leadActivity).default([]),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
});

export type Lead = z.infer<typeof lead>;

export const crmConfig = z.object({
  version: z.string(),
  pipelines: z.array(pipeline).default([]),
  /** Distribuicao automatica de leads entre corretores. */
  assignment: z
    .object({
      strategy: z.enum(['round_robin', 'least_open', 'by_city', 'manual']).default('round_robin'),
      /** Reatribui se o responsavel nao tocar no lead em N horas. */
      reassignAfterHours: z.number().int().min(0).default(0),
    })
    .default({}),
  /** Motivos de perda oferecidos ao fechar como `lost`. */
  lossReasons: z.array(z.object({ key: configKey, label: editableText })).default([]),
});

export type CrmConfig = z.infer<typeof crmConfig>;
